// src/pages/ClientsPage.tsx
import React, { useMemo, useState } from "react";
import { useData } from "../DataContext";
import { generateId } from "../utils/id";
import { Client } from "../data/mockData";
import { useTranslation } from "react-i18next";

export default function ClientsPage() {
  const { clients, addClient, updateClient, deleteClient } = useData();
  const { t } = useTranslation();

  // Add form
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  // Page helpers
  const [search, setSearch] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Client | null>(null);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = [...((clients as Client[]) || [])].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    if (!q) return list;
    return list.filter((c) => {
      const hay = `${c.id} ${c.name} ${c.address || ""} ${c.phone || ""} ${
        c.email || ""
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [clients, search]);

  const resetAddForm = () => {
    setName("");
    setAddress("");
    setPhone("");
    setEmail("");
    setFormError(null);
  };

  const handleAdd = () => {
    const n = name.trim();
    if (!n) {
      setFormError(t("clients.nameRequired"));
      return;
    }

    addClient({
      id: generateId("CL"),
      name: n,
      address: address.trim(),
      phone: phone.trim(),
      email: email.trim(),
    });

    resetAddForm();
  };

  const beginEdit = (c: Client) => {
    setEditingId(c.id);
    setEditDraft({ ...c });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const saveEdit = () => {
    if (!editDraft) return;

    const n = editDraft.name.trim();
    if (!n) {
      alert(t("clients.nameRequiredAlert"));
      return;
    }

    updateClient({
      ...editDraft,
      name: n,
      address: (editDraft.address || "").trim(),
      phone: (editDraft.phone || "").trim(),
      email: (editDraft.email || "").trim(),
    });

    setEditingId(null);
    setEditDraft(null);
  };

  const handleDelete = (clientId: string) => {
    const ok = window.confirm(t("clients.deleteConfirm"));
    if (!ok) return;
    deleteClient(clientId);
  };

  return (
    <div className="container">
      <style>{`
        /* Fix bleed/overlap by NOT reusing .formGrid here.
           Use minmax(0, ...) so inputs can shrink instead of overflowing. */
        .clientFormGrid{
          display:grid;
          grid-template-columns: minmax(0, 1.6fr) minmax(0, 0.9fr) minmax(0, 1.2fr);
          gap: 14px;
          align-items: start;
        }
        .clientFormGrid .field{ min-width: 0; }
        .clientFormGrid .input{ width: 100%; box-sizing: border-box; }
        .clientFormGrid .fullRow{ grid-column: 1 / -1; }

        @media (max-width: 980px){
          .clientFormGrid{ grid-template-columns: 1fr; }
          .clientFormGrid .fullRow{ grid-column: auto; }
        }

        /* Icon actions */
        .actionIcons{
          display:flex;
          gap:10px;
          align-items:center;
        }
        .iconActionBtn{
          height: 36px;
          width: 36px;
          border-radius: 999px;
          display:grid;
          place-items:center;
          cursor:pointer;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          box-shadow: var(--shadow-sm);
        }
        .iconActionBtn:hover{ background: var(--table-row-hover); }
        .iconActionBtn.primary{
          border-color: rgba(79,107,237,0.28);
          color: var(--primary);
        }
        .iconActionBtn.danger{
          border-color: rgba(229,83,61,0.28);
          color: var(--bad);
        }
        .iconGlyph{
          font-size: 14px;
          line-height: 1;
          font-weight: 900;
        }
      `}</style>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ marginBottom: 6 }}>{t("clients.title")}</h1>
          <div className="pageNote">
            {t("clients.pageNote")}
          </div>
        </div>

        {/* Quick search (top-right standard) */}
        <div style={{ minWidth: 280, flex: 1, maxWidth: 520 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              color: "var(--pageMuted)",
            }}
          >
            {t("common.search")}
          </label>
          <input
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("clients.searchPlaceholder")}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {/* ADD CLIENT */}
      <div className="card cardTight" style={{ marginTop: 14, marginBottom: 14 }}>
        <div className="cardHeader">
          <h3 className="cardTitle">{t("clients.addTitle")}</h3>
          <div className="cardSub">
            <span className="reqStar">*</span> {t("common.required")}
          </div>
        </div>

        <div className="clientFormGrid">
          <div className="field">
            <label className="label">
              {t("common.name")} <span className="reqStar">*</span>
            </label>
            <input
              className="input"
              placeholder={t("clients.namePlaceholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (formError) setFormError(null);
              }}
            />
          </div>

          <div className="field">
            <label className="label">{t("common.phone")}</label>
            <input
              className="input"
              placeholder={t("clients.phonePlaceholder")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="label">{t("common.email")}</label>
            <input
              className="input"
              placeholder={t("clients.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="field fullRow">
            <label className="label">{t("common.address")}</label>
            <input
              className="input"
              placeholder={t("clients.addressPlaceholder")}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
        </div>

        {formError && (
          <div className="formError" role="alert">
            {formError}
          </div>
        )}

        <div className="formActions">
          <button className="btn" onClick={handleAdd} type="button">
            {t("clients.addTitle")}
          </button>
          <button className="btn btnSecondary" onClick={resetAddForm} type="button">
            {t("common.clear")}
          </button>
        </div>
      </div>

      {/* CLIENTS TABLE */}
      <div className="card">
        <div className="cardHeader">
          <h3 className="cardTitle">{t("clients.listTitle")}</h3>
          <div className="cardSub">{t("clients.clientCount", { count: filteredClients.length })}</div>
        </div>

        <div className="tableWrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 110 }}>{t("common.id")}</th>
                <th>{t("common.name")}</th>
                <th>{t("common.address")}</th>
                <th style={{ width: 150 }}>{t("common.phone")}</th>
                <th style={{ width: 260 }}>{t("common.email")}</th>
                <th style={{ width: 140, textAlign: "right" }}>{t("common.actions")}</th>
              </tr>
            </thead>

            <tbody>
              {filteredClients.map((c) => {
                const isEditing = editingId === c.id;

                if (isEditing && editDraft) {
                  return (
                    <tr key={c.id} style={{ background: "rgba(37,99,235,0.06)" }}>
                      <td style={{ fontWeight: 800 }}>{c.id}</td>

                      <td>
                        <input
                          className="input"
                          value={editDraft.name}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, name: e.target.value })
                          }
                          style={{ width: "100%" }}
                        />
                      </td>

                      <td>
                        <input
                          className="input"
                          value={editDraft.address || ""}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, address: e.target.value })
                          }
                          style={{ width: "100%" }}
                        />
                      </td>

                      <td>
                        <input
                          className="input"
                          value={editDraft.phone || ""}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, phone: e.target.value })
                          }
                          style={{ width: "100%" }}
                        />
                      </td>

                      <td>
                        <input
                          className="input"
                          value={editDraft.email || ""}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, email: e.target.value })
                          }
                          style={{ width: "100%" }}
                        />
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <div className="actionsRow" style={{ justifyContent: "flex-end" }}>
                          <button className="btn btnSm" onClick={saveEdit} type="button">
                            {t("common.save")}
                          </button>
                          <button
                            className="btn btnSecondary btnSm"
                            onClick={cancelEdit}
                            type="button"
                          >
                            {t("common.cancel")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 800 }}>{c.id}</td>
                    <td style={{ fontWeight: 800 }}>{c.name}</td>
                    <td style={{ color: "var(--muted)" }}>
                      <div className="cellClamp" title={c.address || ""}>
                        {c.address || "—"}
                      </div>
                    </td>
                    <td>{c.phone || "—"}</td>
                    <td>
                      <div className="cellClamp" title={c.email || ""}>
                        {c.email || "—"}
                      </div>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div className="actionIcons" style={{ justifyContent: "flex-end" }}>
                        <button
                          className="iconActionBtn primary"
                          onClick={() => beginEdit(c)}
                          type="button"
                          title={t("clients.editAriaLabel")}
                          aria-label={t("clients.editAriaLabel")}
                        >
                          <span className="iconGlyph">✎</span>
                        </button>

                        <button
                          className="iconActionBtn danger"
                          onClick={() => handleDelete(c.id)}
                          type="button"
                          title={t("clients.deleteAriaLabel")}
                          aria-label={t("clients.deleteAriaLabel")}
                        >
                          <span className="iconGlyph">🗑</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {filteredClients.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    style={{ color: "var(--muted)", fontStyle: "italic", padding: 14 }}
                  >
                    {t("clients.noClients")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pageNote" style={{ marginTop: 10 }}>
          {t("clients.searchTip")}
        </div>
      </div>
    </div>
  );
}
